import React, { useContext, useEffect, useState } from "react";
import { StyleSheet, TouchableOpacity, TextInput } from "react-native";
import { Text, View } from "../../shared/Themed";
import FeatherIcon from "react-native-vector-icons/Feather";
import { ReadOnlyProps } from "../../../utils/templates";
import DeviceOrientationContext from "../../context/DeviceOrientationContext";
import { exportDataToExcel } from "../../../utils/exportDataToExcel";
import {
  getSalesSearchOperations,
  getSearchOperations,
} from "../../../services/operationService";
import { useSelector } from "react-redux";

interface Props {
  childern?: React.ElementType;
  titleName: string;
  isSmallDevice?: boolean;
  isMobileDevice?: boolean;
  isEditable?: boolean;
  ref?: any;
  isResetFields?: boolean;
  setRefresh: (event: any) => void;
  setEditable?: (event: any) => void;

  //for excel download
  dataToExport?: Array<unknown>;
  fileName?: string;
  sheetName?: string;

  // J
  padding?: boolean;
  disableTitle?: boolean;
  noBorder?: boolean;
}

export const SearchOperationLabelBannerWithSearchBar = (
  props: ReadOnlyProps<Props>
) => {
  const Children = (props.childern || {}) as React.ElementType;
  const { isSmallDevice, isMobileDevice } = useContext(
    DeviceOrientationContext
  );
  const userInfo = useSelector((state: any) => state.configurations.userInfo);

  const [searchTerm, setSearchTerm] = useState<string>("");

  const handleSearch = (searchTerm: string) => {
    if (searchTerm && searchTerm != "") {
      getSalesSearchOperations(userInfo.login, {}, {}, searchTerm);
    }
  };

  useEffect(() => {
    // if search text is empty, then return all data
    if (searchTerm && searchTerm.trim().length === 0) {
      handleSearch("");
      return;
    }

    handleSearch(searchTerm.trim());
  }, [searchTerm]);

  useEffect(() => {
    if (props.isResetFields) {
      setSearchTerm("");
    }
  }, [props.isResetFields]);

  const submitSearch = () => {
    handleSearch(searchTerm.trim());
  };

  const isExportAllowed = () => {
    return (
      !isSmallDevice &&
      !isMobileDevice &&
      props.dataToExport &&
      props.dataToExport.length > 0
    );
  };

  const showInlineTitle = !isSmallDevice && !isMobileDevice && props.disableTitle == undefined;

  return (
    <View>
      <View style={styles.container}>
        {/* Search row */}
        <View
          style={[
            styles.searchRow,
            showInlineTitle && { justifyContent: "space-between" },
            props.isEditable && { backgroundColor: "#FEF3C7" },
          ]}
        >
          {/* Inline title for desktop */}
          {showInlineTitle && (
            <Text style={styles.inlineTitle}>{props.titleName}</Text>
          )}

          {/* Search controls - grouped on right for desktop */}
          <View style={[
            styles.searchControls,
            !showInlineTitle && { width: "100%", justifyContent: "flex-end" },
          ]}>
            {props.isEditable && (
              <TouchableOpacity
                style={styles.cancelButton}
                onPress={() => props.setEditable?.(!props.isEditable)}
              >
                <Text style={styles.cancelButtonText}>Cancel</Text>
              </TouchableOpacity>
            )}

            <TouchableOpacity
              style={styles.refreshButton}
              onPress={() => {
                setSearchTerm("");
                props.setRefresh(true);
              }}
            >
              <FeatherIcon name="refresh-cw" size={15} color="#6B7280" />
            </TouchableOpacity>

            <View style={[
              styles.searchContainer,
              showInlineTitle && styles.searchContainerCompact,
            ]}>
              <FeatherIcon
                name="search"
                size={16}
                color="#9CA3AF"
                style={{ marginRight: 8 }}
              />
              <TextInput
                ref={props.ref}
                style={styles.searchInput}
                value={searchTerm || ""}
                placeholder="Search..."
                placeholderTextColor="#9CA3AF"
                onChangeText={(text) => setSearchTerm(text)}
                onSubmitEditing={() =>
                  searchTerm.trim().length < 3 ? {} : submitSearch()
                }
              />
            </View>

            {isExportAllowed() && (
              <TouchableOpacity
                style={styles.exportButton}
                onPress={() =>
                  exportDataToExcel(
                    props.dataToExport || [],
                    props.fileName,
                    props.sheetName,
                  )
                }
              >
                <FeatherIcon name="download" size={15} color="#6B7280" />
              </TouchableOpacity>
            )}
          </View>
        </View>

        {props.childern && <Children {...props} />}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    width: "100%",
  },
  titleRow: {
    paddingHorizontal: 4,
    paddingBottom: 8,
  },
  title: {
    fontSize: 14,
    fontWeight: "600",
    color: "#374151",
  },
  inlineTitle: {
    fontSize: 14,
    fontWeight: "700",
    color: "#111827",
    flexShrink: 0,
  },
  searchRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    width: "100%",
    paddingHorizontal: 12,
  },
  searchControls: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  searchContainerCompact: {
    width: 250,
    flex: undefined,
  },
  searchContainer: {
    flex: 1,
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#ffffff",
    borderWidth: 1,
    borderColor: "#E2E8F0",
    borderRadius: 10,
    paddingHorizontal: 12,
    height: 40,
  },
  searchInput: {
    flex: 1,
    fontSize: 14,
    color: "#111827",
    height: 40,
    outlineStyle: "none",
  } as any,
  refreshButton: {
    width: 40,
    height: 40,
    borderRadius: 10,
    backgroundColor: "#F3F4F6",
    justifyContent: "center",
    alignItems: "center",
    borderWidth: 1,
    borderColor: "#E2E8F0",
  },
  exportButton: {
    width: 40,
    height: 40,
    borderRadius: 10,
    backgroundColor: "#F3F4F6",
    justifyContent: "center",
    alignItems: "center",
    borderWidth: 1,
    borderColor: "#E2E8F0",
  },
  cancelButton: {
    paddingVertical: 8,
    paddingHorizontal: 14,
    borderRadius: 8,
    backgroundColor: "#6B7280",
  },
  cancelButtonText: {
    color: "#fff",
    fontSize: 13,
    fontWeight: "600",
  },
});
